import { getCurrentFormatterLocale } from './localeFormatting';

const DEFAULT_CURRENCY = 'USD';
const DEFAULT_EMPTY_VALUE = '\u2014';

function getLocaleNumberSeparators(options = {}) {
  const parts = new Intl.NumberFormat(resolveFormatterLocale(options)).formatToParts(1234.5);

  return {
    decimalSeparator: parts.find(part => part.type === 'decimal')?.value || '.',
    groupSeparator: parts.find(part => part.type === 'group')?.value || ',',
  };
}

function toFiniteNumber(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value === 'string') {
    const directNumber = Number(value);
    if (Number.isFinite(directNumber)) return directNumber;

    const hasLeadingCurrencyToken = /^\s*[^\d.,-\s]+/.test(value);
    const parsedNumber = Number(parseCurrencyInput(value, { preferSingleDotDecimal: hasLeadingCurrencyToken }));
    return Number.isFinite(parsedNumber) ? parsedNumber : null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

export function normalizeNativeDigits(value) {
  return String(value ?? '')
    .replace(/[\u0660-\u0669]/g, digit => String(digit.charCodeAt(0) - 0x0660))
    .replace(/[\u06f0-\u06f9]/g, digit => String(digit.charCodeAt(0) - 0x06f0))
    .replace(/[\u0966-\u096f]/g, digit => String(digit.charCodeAt(0) - 0x0966));
}

export function parseCurrencyInput(value, options = {}) {
  const numericText = normalizeNativeDigits(value || '')
    .replace(/\u2212/g, '-')
    .replace(/[^\d.,\u066b-]/g, '');
  const separatorMatches = [...numericText.matchAll(/[.,\u066b]/g)];

  if (!numericText) return '';

  const isNegative = numericText.trim().startsWith('-');
  const unsignedText = numericText.replace(/-/g, '');

  if (separatorMatches.length === 0) {
    return `${isNegative ? '-' : ''}${unsignedText.replace(/\D/g, '')}`;
  }

  const separators = [...unsignedText.matchAll(/[.,\u066b]/g)];
  const hasComma = unsignedText.includes(',');
  const hasDot = unsignedText.includes('.');
  const lastSeparator = separators[separators.length - 1];
  const { decimalSeparator, groupSeparator } = getLocaleNumberSeparators(options);
  const arabicDecimalIndex = unsignedText.lastIndexOf('\u066b');
  let decimalSeparatorIndex = -1;

  // Arabic's explicit decimal separator is never a thousands separator.
  if (arabicDecimalIndex !== -1) {
    decimalSeparatorIndex = arabicDecimalIndex;
  } else if (hasComma && hasDot) {
    decimalSeparatorIndex = lastSeparator.index;
  } else if (separators.length === 1) {
    const separatorIndex = separators[0].index;
    const separatorChar = unsignedText[separatorIndex];
    const digitsBeforeSeparator = unsignedText.slice(0, separatorIndex).replace(/\D/g, '');
    const digitsAfterSeparator = unsignedText.slice(separatorIndex + 1).replace(/\D/g, '');
    const isGroupingCandidate = digitsBeforeSeparator.length > 0
      && digitsBeforeSeparator.length <= 3
      && digitsAfterSeparator.length === 3;
    const shouldPreferThousands = options.preferThousandsForAmbiguousInput
      && separatorChar === groupSeparator
      && isGroupingCandidate;
    const shouldPreferSingleDotDecimal = options.preferSingleDotDecimal
      && unsignedText[separatorIndex] === '.';

    if (
      digitsBeforeSeparator.length === 0
      || digitsBeforeSeparator === '0'
      || separatorChar === decimalSeparator
      || shouldPreferSingleDotDecimal
      || (!shouldPreferThousands && digitsAfterSeparator.length !== 3)
    ) {
      decimalSeparatorIndex = separatorIndex;
    }
  } else {
    const separatorChar = unsignedText[separators[0].index];
    const groups = unsignedText.split(separatorChar);
    const hasNonGroupingTail = groups[groups.length - 1].replace(/\D/g, '').length !== 3;

    if (hasNonGroupingTail) {
      decimalSeparatorIndex = lastSeparator.index;
    }
  }

  const parsedText = unsignedText
    .replace(/[.,\u066b]/g, (separator, index) => (
      index === decimalSeparatorIndex ? '.' : ''
    ))
    .replace(/[^\d.]/g, '');

  return `${isNegative ? '-' : ''}${parsedText}`;
}

function resolveFormatterLocale(options = {}) {
  return getCurrentFormatterLocale(options.language || options.locale, options.localeFallback || 'en');
}

function createNumberFormatter(options = {}) {
  return new Intl.NumberFormat(resolveFormatterLocale(options), options.formatOptions || {});
}

function formatUnit(value, unit, options = {}) {
  return createNumberFormatter({
    ...options,
    formatOptions: {
      style: 'unit',
      unit,
      unitDisplay: options.unitDisplay || 'narrow',
      minimumFractionDigits: options.minimumFractionDigits,
      maximumFractionDigits: options.maximumFractionDigits,
    },
  }).format(value);
}

export const formatNumber = (value, options = {}) => {
  const numericValue = toFiniteNumber(value);
  if (numericValue == null) return options.fallback ?? '';

  return createNumberFormatter({
    ...options,
    formatOptions: {
      maximumFractionDigits: options.maximumFractionDigits ?? 0,
      minimumFractionDigits: options.minimumFractionDigits,
      ...options.formatOptions,
    },
  }).format(numericValue);
};

export const formatCompactNumber = (value, options = {}) => {
  const numericValue = toFiniteNumber(value);
  if (numericValue == null) return options.fallback ?? '';

  return createNumberFormatter({
    ...options,
    formatOptions: {
      notation: 'compact',
      compactDisplay: 'short',
      maximumFractionDigits: options.maximumFractionDigits ?? 1,
      minimumFractionDigits: options.minimumFractionDigits,
      ...options.formatOptions,
    },
  }).format(numericValue);
};

export const formatCurrency = (value, options = {}) => {
  const numericValue = toFiniteNumber(value);
  if (numericValue == null) return options.fallback ?? '';

  return createNumberFormatter({
    ...options,
    formatOptions: {
      style: 'currency',
      currency: options.currency || DEFAULT_CURRENCY,
      maximumFractionDigits: options.maximumFractionDigits ?? 2,
      minimumFractionDigits: options.minimumFractionDigits ?? 0,
      ...options.formatOptions,
    },
  }).format(numericValue);
};

export const formatCurrencyInput = (value, info, options = {}) => {
  if (info?.userTyping) return String(info.input ?? value ?? '');

  // Avoid ambiguous separators when users append or delete digits in an amount.
  return formatCurrency(value, { ...options, formatOptions: { ...options.formatOptions, useGrouping: false } });
};

export const formatPrice = (price, options = {}) => {
  const numericValue = toFiniteNumber(price);
  if (numericValue == null) return options.fallback ?? '';
  if (numericValue === 0) return options.freeLabel ?? formatCurrency(0, options);

  return formatCurrency(numericValue, {
    ...options,
    maximumFractionDigits: options.maximumFractionDigits ?? 2,
    minimumFractionDigits: options.minimumFractionDigits ?? 0,
  });
};

export const formatBalance = (balance, options = {}) => {
  const locale = resolveFormatterLocale(options);
  const fallback = options.fallback ?? formatCurrency(0, {
    ...options,
    language: locale,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const numericValue = toFiniteNumber(balance);

  if (numericValue == null) return fallback;

  return formatCurrency(numericValue, {
    ...options,
    language: locale,
    minimumFractionDigits: options.minimumFractionDigits ?? 0,
    maximumFractionDigits: options.maximumFractionDigits ?? 2,
  });
};

export const formatRecordCount = (count, options = {}) => {
  const numericValue = toFiniteNumber(count);
  if (numericValue == null) return options.fallback ?? '';

  if (Math.abs(numericValue) >= (options.compactThreshold ?? 1_000)) {
    return formatCompactNumber(numericValue, options);
  }

  return formatNumber(numericValue, {
    ...options,
    maximumFractionDigits: options.maximumFractionDigits ?? 0,
  });
};

export const formatDuration = (seconds, options = {}) => {
  const numericValue = toFiniteNumber(seconds);
  if (numericValue == null) return options.emptyLabel ?? DEFAULT_EMPTY_VALUE;

  if (numericValue < 60) {
    return formatUnit(numericValue, 'second', {
      ...options,
      maximumFractionDigits: options.maximumFractionDigits ?? 1,
      minimumFractionDigits: options.minimumFractionDigits ?? (Number.isInteger(numericValue) ? 0 : 1),
    });
  }

  if (numericValue < 3600) {
    const minutes = Math.floor(numericValue / 60);
    const remainingSeconds = Number((numericValue % 60).toFixed(options.maximumFractionDigits ?? 1));

    if (remainingSeconds === 0) {
      return formatUnit(minutes, 'minute', { ...options, maximumFractionDigits: 0 });
    }

    return [
      formatUnit(minutes, 'minute', { ...options, maximumFractionDigits: 0 }),
      formatUnit(remainingSeconds, 'second', {
        ...options,
        maximumFractionDigits: options.maximumFractionDigits ?? 1,
        minimumFractionDigits: Number.isInteger(remainingSeconds) ? 0 : 1,
      }),
    ].join(' ');
  }

  const hours = Math.floor(numericValue / 3600);
  const remainingMinutes = Math.floor((numericValue % 3600) / 60);

  if (remainingMinutes === 0) {
    return formatUnit(hours, 'hour', { ...options, maximumFractionDigits: 0 });
  }

  return [
    formatUnit(hours, 'hour', { ...options, maximumFractionDigits: 0 }),
    formatUnit(remainingMinutes, 'minute', { ...options, maximumFractionDigits: 0 }),
  ].join(' ');
};
